import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle2,
  Eye,
  FileText,
  FlaskConical,
  MapPin,
  Paperclip,
  Trash2,
  Upload,
  User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppModal } from '../../../components/ui/app-modal';
import { Button } from '../../../components/ui/button';
import { DatetimePicker } from '../../../components/ui/datetimePicker';
import { FileDropZone } from '../../../components/ui/file-drop-zone';
import { usePermissions } from '../../../hooks/use-permissions';
import { api } from '../../../lib/ky';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';
import {
  AttachmentPreviewModal,
  CompleteModal,
  type PreviewAttachment,
  RescheduleModal,
  type SiteVisitStatus,
  StatusBadge,
  fmt,
} from './shared-visit-ui';

// ── Types ──────────────────────────────────────────────────────────────────────

type LabReportStatus = 'DRAFT' | 'SUBMITTED';
type LabRequestStatus =
  | 'PENDING_LAB_ASSIGNMENT'
  | 'ASSIGNED_TO_LAB_TECHNICIAN'
  | 'IN_PROGRESS'
  | 'SUBMITTED_TO_CONSULTANT';

interface LinkedSiteVisit {
  id: string;
  visitType: string;
  status: SiteVisitStatus;
  scheduledAt: string | null;
  address: string | null;
}

interface LabReportAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedAt: string;
}

interface LabReport {
  id: string;
  name: string | null;
  comments: string | null;
  status: LabReportStatus;
  submittedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string };
  attachments: LabReportAttachment[];
}

interface LabRequest {
  id: string;
  description: string | null;
  status: LabRequestStatus;
  assignedLabTechnician: { id: string; name: string } | null;
  createdAt: string;
  createdBy: { id: string; name: string };
  siteVisit: LinkedSiteVisit | null;
  reports: LabReport[];
}

// ── Config ─────────────────────────────────────────────────────────────────────

const LAB_STATUS_CFG: Record<LabRequestStatus, { label: string; className: string }> = {
  PENDING_LAB_ASSIGNMENT: {
    label: 'Pending Lab Assignment',
    className: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  ASSIGNED_TO_LAB_TECHNICIAN: {
    label: 'Assigned to Lab Technician',
    className: 'bg-brand-50 text-brand-600 border-brand-200',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  SUBMITTED_TO_CONSULTANT: {
    label: 'Submitted to Consultant',
    className: 'bg-success-50 text-success-600 border-success-200',
  },
};

function LabStatusBadge({ status }: { status: LabRequestStatus }) {
  const cfg = LAB_STATUS_CFG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold shrink-0',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function fmtBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Create Lab Request Modal ───────────────────────────────────────────────────

function CreateLabRequestModal({
  open,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (description: string) => void;
  isPending: boolean;
}) {
  const [description, setDescription] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(description);
  }

  return (
    <AppModal open={open} onClose={onClose} title="Request Lab Test">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Description / Scope
            <span className="ml-1 text-gray-400">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="Describe the lab test scope…"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isPending}>
            {isPending ? 'Creating…' : 'Create Request'}
          </Button>
        </div>
      </form>
    </AppModal>
  );
}

// ── Schedule Lab Site Visit Modal ──────────────────────────────────────────────

function ScheduleLabVisitModal({
  open,
  defaultAddress,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  defaultAddress?: string;
  onClose: () => void;
  onSubmit: (form: { name: string; address: string; visitDate: string; comments: string }) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState({ name: '', address: '', visitDate: '', comments: '' });

  useEffect(() => {
    if (open) setForm({ name: '', address: defaultAddress ?? '', visitDate: '', comments: '' });
  }, [open, defaultAddress]);

  const isValid = !!form.visitDate && form.name.trim().length > 0;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Schedule Lab Site Visit"
      description="Visit name and scheduled date & time are required — other fields can be filled in later."
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="lab-visit-name">
            Visit Name <span className="text-error-500">*</span>
          </label>
          <input
            id="lab-visit-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Sample Collection"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="lab-visit-address">
            Location / Address
          </label>
          <input
            id="lab-visit-address"
            type="text"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">
            Scheduled Date &amp; Time <span className="text-error-500">*</span>
          </label>
          <DatetimePicker
            value={form.visitDate ? new Date(form.visitDate) : undefined}
            onChange={(date) =>
              setForm((f) => ({ ...f, visitDate: date ? date.toISOString() : '' }))
            }
            granularity="minute"
            placeholder="Pick date & time"
            className="w-full"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="lab-visit-comments">
            Comments
          </label>
          <textarea
            id="lab-visit-comments"
            rows={3}
            value={form.comments}
            onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
            placeholder="Any notes or instructions…"
            className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={() => onSubmit(form)}
            disabled={!isValid || isPending}
          >
            {isPending ? 'Scheduling…' : 'Schedule Visit'}
          </Button>
        </div>
      </div>
    </AppModal>
  );
}

// ── Report Editor (single report per lab request) ──────────────────────────────

function ReportEditor({
  labRequest,
  studyRequestId,
  canAct,
  onMutated,
}: {
  labRequest: LabRequest;
  studyRequestId: string;
  canAct: boolean;
  onMutated: () => void;
}) {
  const report = labRequest.reports[0] ?? null;
  const isLocked = labRequest.status === 'SUBMITTED_TO_CONSULTANT';
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(report?.name ?? '');
  const [comments, setComments] = useState(report?.comments ?? '');
  const [newReportFile, setNewReportFile] = useState<File | null>(null);
  const [newReportFileError, setNewReportFileError] = useState('');
  const [previewAttachment, setPreviewAttachment] = useState<PreviewAttachment | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const base = `study-requests/${studyRequestId}/lab-requests/${labRequest.id}`;

  async function handlePreview(att: LabReportAttachment) {
    setPreviewingId(att.id);
    try {
      const res = await api
        .get(`${base}/reports/${report?.id}/attachments/${att.id}/download`)
        .json<{ url: string; fileName: string }>();
      setPreviewAttachment({ fileName: res.fileName, mimeType: att.mimeType, url: res.url });
    } finally {
      setPreviewingId(null);
    }
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const created = await api
        .post(`${base}/reports`, {
          json: { name: name.trim(), comments: comments || undefined },
        })
        .json<{ id: string }>();
      const fd = new FormData();
      fd.append('file', newReportFile as File);
      await api.post(`${base}/reports/${created.id}/attachments`, { body: fd }).json();
    },
    onSuccess: () => {
      setNewReportFile(null);
      onMutated();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api
        .patch(`${base}/reports/${report?.id}`, {
          json: { name: name || undefined, comments: comments || undefined },
        })
        .json(),
    onSuccess: () => {
      setEditing(false);
      onMutated();
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => api.post(`${base}/reports/${report?.id}/submit`).json(),
    onSuccess: onMutated,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`${base}/reports/${report?.id}/attachments`, { body: fd }).json();
    },
    onSuccess: onMutated,
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) =>
      api.delete(`${base}/reports/${report?.id}/attachments/${attachmentId}`),
    onSuccess: onMutated,
  });

  const blockedReason =
    labRequest.status === 'PENDING_LAB_ASSIGNMENT'
      ? 'A Lab Technician must self-assign this request before uploading a report.'
      : labRequest.siteVisit && labRequest.siteVisit.status !== 'COMPLETED'
        ? 'Complete the lab site visit before uploading the report.'
        : null;

  if (!report) {
    return (
      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Lab Test Report
        </p>
        {canAct && !blockedReason ? (
          <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
            <div>
              <label
                className="mb-1 block text-xs font-medium text-gray-600"
                htmlFor="new-report-name"
              >
                Report Name <span className="text-error-500">*</span>
              </label>
              <input
                id="new-report-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Report name"
                className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label
                className="mb-1 block text-xs font-medium text-gray-600"
                htmlFor="new-report-comments"
              >
                Comments / Findings
                <span className="ml-1 text-gray-400">(optional)</span>
              </label>
              <textarea
                id="new-report-comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Comments / findings…"
                className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Lab Test Report File <span className="text-error-500">*</span>
              </label>
              <FileDropZone
                file={newReportFile}
                onChange={setNewReportFile}
                onError={setNewReportFileError}
                hint="PDF, DOCX, JPG, PNG — max 10 MB"
              />
              {newReportFileError && (
                <p className="mt-1 text-xs text-error-500">{newReportFileError}</p>
              )}
            </div>
            {createMutation.isError && (
              <p className="text-xs text-error-500">Failed to create report. Try again.</p>
            )}
            <Button
              size="sm"
              disabled={
                !name.trim() || !newReportFile || !!newReportFileError || createMutation.isPending
              }
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? 'Creating…' : 'Create Report'}
            </Button>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center">
            <FileText size={24} className="mx-auto mb-2 text-gray-200" />
            <p className="text-xs text-gray-400">{blockedReason ?? 'No lab report yet'}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Lab Test Report
        </p>
        <span
          className={cn(
            'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
            report.status === 'SUBMITTED'
              ? 'bg-success-50 text-success-600 border-success-200'
              : 'bg-gray-100 text-gray-600 border-gray-200',
          )}
        >
          {report.status === 'SUBMITTED' ? 'Submitted' : 'Draft'}
        </span>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          {editing ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Report name…"
              className="rounded-lg border border-brand-300 bg-white px-2.5 py-1 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          ) : (
            <p className="text-sm font-semibold text-gray-800">
              {report.name || 'Untitled Report'}
            </p>
          )}
        </div>

        {editing ? (
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Comments / findings…"
            className="mb-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        ) : (
          report.comments && (
            <p className="mb-3 text-sm text-gray-600 leading-relaxed">{report.comments}</p>
          )
        )}

        {report.attachments.length > 0 && (
          <div className="mb-3 space-y-1.5">
            {report.attachments.map((att) => (
              <div
                key={att.id}
                className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5"
              >
                <Paperclip size={12} className="shrink-0 text-gray-400" />
                <span className="flex-1 truncate text-xs font-medium text-gray-700">
                  {att.fileName}
                </span>
                <span className="shrink-0 text-xs text-gray-400">
                  {fmtBytes(att.fileSizeBytes)}
                </span>
                <span className="shrink-0 text-xs text-gray-400">{fmtDate(att.uploadedAt)}</span>
                <button
                  type="button"
                  onClick={() => handlePreview(att)}
                  disabled={previewingId === att.id}
                  className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 disabled:opacity-50"
                >
                  <Eye size={11} />
                  {previewingId === att.id ? 'Loading…' : 'Preview'}
                </button>
                {!isLocked && canAct && (
                  <button
                    type="button"
                    onClick={() => deleteAttachmentMutation.mutate(att.id)}
                    className="shrink-0 text-gray-400 hover:text-error-500"
                    aria-label="Delete attachment"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mb-3 flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <User size={11} />
            {report.createdBy.name}
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={11} />
            {fmtDate(report.createdAt)}
          </span>
          {report.submittedAt && (
            <span className="flex items-center gap-1 text-success-500">
              <CheckCircle2 size={11} />
              Submitted {fmtDate(report.submittedAt)}
            </span>
          )}
        </div>

        {!isLocked && canAct && (
          <div className="flex flex-wrap items-center gap-2">
            {editing ? (
              <>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={updateMutation.isPending}
                  onClick={() => updateMutation.mutate()}
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}

            {!editing && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadMutation.isPending}
                >
                  <Upload size={13} className="mr-1" />
                  {uploadMutation.isPending
                    ? 'Uploading…'
                    : report.attachments.length > 0
                      ? 'Replace File'
                      : 'Attach File'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      uploadMutation.mutate(file);
                      e.target.value = '';
                    }
                  }}
                />

                <Button
                  size="sm"
                  disabled={submitMutation.isPending || report.attachments.length === 0}
                  onClick={() => submitMutation.mutate()}
                >
                  <CheckCircle2 size={13} className="mr-1" />
                  {submitMutation.isPending ? 'Submitting…' : 'Submit Report'}
                </Button>
              </>
            )}

            {!editing && report.attachments.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Attach a file before you can submit this report.
              </p>
            )}
            {uploadMutation.isError && (
              <p className="text-xs text-error-500">Upload failed. Try again.</p>
            )}
            {submitMutation.isError && (
              <p className="text-xs text-error-500">Submit failed. Try again.</p>
            )}
          </div>
        )}
      </div>

      <AttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}

// ── Lab Request Row ────────────────────────────────────────────────────────────

function LabRequestRow({
  req,
  studyRequestId,
  defaultAddress,
  scheduleVisitTrigger,
  onMutated,
}: {
  req: LabRequest;
  studyRequestId: string;
  defaultAddress?: string;
  scheduleVisitTrigger?: number;
  onMutated?: () => void;
}) {
  const { can } = usePermissions();
  const { user } = useAuthStore();
  const canSubmitReport = can('sm-lab:submit-report');
  const isAssignee = !!user && req.assignedLabTechnician?.id === user.id;
  const [scheduleVisitOpen, setScheduleVisitOpen] = useState(false);
  const [completeVisitOpen, setCompleteVisitOpen] = useState(false);
  const [rescheduleVisitOpen, setRescheduleVisitOpen] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (scheduleVisitTrigger && isAssignee && !req.siteVisit) {
      setScheduleVisitOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleVisitTrigger]);

  const labKey = ['lab-requests', studyRequestId];
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: labKey });
    onMutated?.();
  };
  const base = `study-requests/${studyRequestId}`;

  const createVisitMutation = useMutation({
    mutationFn: (form: { name: string; address: string; visitDate: string; comments: string }) =>
      api
        .post(`${base}/lab-requests/${req.id}/site-visit`, {
          json: {
            name: form.name || undefined,
            address: form.address || undefined,
            visitDate: form.visitDate || undefined,
            comments: form.comments || undefined,
          },
        })
        .json(),
    onSuccess: () => {
      setScheduleVisitOpen(false);
      invalidate();
    },
  });

  const startVisitMutation = useMutation({
    mutationFn: () => api.patch(`${base}/site-visits/${req.siteVisit?.id}/start`).json(),
    onSuccess: invalidate,
  });

  const completeVisitMutation = useMutation({
    mutationFn: (comments: string) =>
      api
        .patch(`${base}/site-visits/${req.siteVisit?.id}/complete`, {
          json: comments ? { comments } : {},
        })
        .json(),
    onSuccess: () => {
      setCompleteVisitOpen(false);
      invalidate();
    },
  });

  const rescheduleVisitMutation = useMutation({
    mutationFn: (form: { scheduledAt: string; reason: string }) =>
      api.patch(`${base}/site-visits/${req.siteVisit?.id}/reschedule`, { json: form }).json(),
    onSuccess: () => {
      setRescheduleVisitOpen(false);
      invalidate();
    },
  });

  const canActOnReport = isAssignee && canSubmitReport;

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* Header row */}
      <div className="flex items-center gap-3 px-5 py-4">
        <FlaskConical size={16} className="shrink-0 text-brand-500" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800">Lab Test Request</p>
          <p className="mt-0.5 text-xs text-gray-400">
            Created by {req.createdBy.name} · {fmtDate(req.createdAt)}
            {req.assignedLabTechnician && <> · Assigned to {req.assignedLabTechnician.name}</>}
          </p>
        </div>

        <LabStatusBadge status={req.status} />
      </div>

      {/* Content */}
      <div className="border-t border-gray-100 px-5 py-4 space-y-5">
        {req.description && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Comment from Beeah Consultant
            </p>
            <p className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              {req.description}
            </p>
          </div>
        )}

        {req.status === 'PENDING_LAB_ASSIGNMENT' && (
          <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
            <p className="text-xs font-medium text-brand-700">
              Unassigned — any Lab Technician can claim this request via the "Assign Lab Request to
              Me" button at the top of the page.
            </p>
          </div>
        )}

        {/* Lab Site Visit */}
        {req.status !== 'PENDING_LAB_ASSIGNMENT' && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Lab Site Visit
            </p>
            {req.siteVisit ? (
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <MapPin size={14} className="shrink-0 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    {req.siteVisit.address && (
                      <p className="text-xs font-medium text-gray-700">{req.siteVisit.address}</p>
                    )}
                    {req.siteVisit.scheduledAt && (
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-400">
                        <Calendar size={10} />
                        {fmt(req.siteVisit.scheduledAt)}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={req.siteVisit.status} />
                </div>
                {isAssignee && req.siteVisit.status !== 'COMPLETED' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(req.siteVisit.status === 'NOT_STARTED' ||
                      req.siteVisit.status === 'APPROVED') && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={startVisitMutation.isPending}
                        onClick={() => startVisitMutation.mutate()}
                      >
                        Mark In Progress
                      </Button>
                    )}
                    {req.siteVisit.status === 'IN_PROGRESS' && (
                      <Button size="sm" onClick={() => setCompleteVisitOpen(true)}>
                        Mark Completed
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRescheduleVisitOpen(true)}
                    >
                      Reschedule
                    </Button>
                  </div>
                )}
              </div>
            ) : isAssignee ? (
              <div className="rounded-xl border border-dashed border-gray-200 p-4 text-center">
                <p className="text-xs text-gray-400">
                  No site visit scheduled — only required if a site inspection is needed. Use the
                  "Schedule Lab Site Visit" button at the top of the page to schedule one.
                </p>
              </div>
            ) : (
              <p className="rounded-xl border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
                No site visit scheduled
              </p>
            )}
          </div>
        )}

        {/* Lab Report */}
        {req.status !== 'PENDING_LAB_ASSIGNMENT' && (
          <ReportEditor
            labRequest={req}
            studyRequestId={studyRequestId}
            canAct={canActOnReport}
            onMutated={invalidate}
          />
        )}
      </div>

      <ScheduleLabVisitModal
        open={scheduleVisitOpen}
        defaultAddress={defaultAddress}
        onClose={() => setScheduleVisitOpen(false)}
        onSubmit={(form) => createVisitMutation.mutate(form)}
        isPending={createVisitMutation.isPending}
      />
      <CompleteModal
        open={completeVisitOpen}
        onClose={() => setCompleteVisitOpen(false)}
        onSubmit={(comments) => completeVisitMutation.mutate(comments)}
        isPending={completeVisitMutation.isPending}
      />
      <RescheduleModal
        open={rescheduleVisitOpen}
        currentScheduledAt={req.siteVisit?.scheduledAt ?? null}
        onClose={() => setRescheduleVisitOpen(false)}
        onSubmit={(form) => rescheduleVisitMutation.mutate(form)}
        isPending={rescheduleVisitMutation.isPending}
      />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function LabRequestsTab({
  requestId,
  defaultAddress,
  autoOpenTrigger,
  scheduleVisitTrigger,
  onMutated,
}: {
  requestId: string;
  defaultAddress?: string;
  autoOpenTrigger?: number;
  scheduleVisitTrigger?: number;
  onMutated?: () => void;
}) {
  const { can } = usePermissions();
  const canCreate = can('sm-lab:create');
  const canView = can('sm-lab:view');
  const [createOpen, setCreateOpen] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (autoOpenTrigger) setCreateOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenTrigger]);

  const labKey = ['lab-requests', requestId];

  const {
    data: labRequests,
    isLoading,
    isError,
  } = useQuery({
    queryKey: labKey,
    queryFn: () => api.get(`study-requests/${requestId}/lab-requests`).json<LabRequest[]>(),
    enabled: canView,
  });

  const createMutation = useMutation({
    mutationFn: (description: string) =>
      api
        .post(`study-requests/${requestId}/lab-requests`, {
          json: { description: description || undefined },
        })
        .json(),
    onSuccess: () => {
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: labKey });
      onMutated?.();
    },
  });

  if (!canView) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <FlaskConical size={32} className="mx-auto mb-3 text-gray-200" />
        <p className="text-sm text-gray-400">You don't have permission to view lab requests.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-2xl bg-gray-100" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="rounded-2xl border border-error-200 bg-error-50 p-6 text-center">
          <p className="text-sm font-medium text-error-600">Failed to load lab requests</p>
          <p className="mt-1 text-xs text-error-500">Please refresh the page</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && (labRequests ?? []).length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center shadow-sm">
          <FlaskConical size={36} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-semibold text-gray-500">No lab test requests yet</p>
          {canCreate && (
            <p className="mt-1 text-xs text-gray-400">
              Use the "Request Lab Test" button at the top of the page to submit one
            </p>
          )}
        </div>
      )}

      {/* Lab request rows */}
      {!isLoading &&
        !isError &&
        (labRequests ?? []).map((req) => (
          <LabRequestRow
            key={req.id}
            req={req}
            studyRequestId={requestId}
            defaultAddress={defaultAddress}
            scheduleVisitTrigger={scheduleVisitTrigger}
            onMutated={onMutated}
          />
        ))}

      {/* Create modal */}
      <CreateLabRequestModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(description) => createMutation.mutate(description)}
        isPending={createMutation.isPending}
      />
    </div>
  );
}
